import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { ProjectBillingModel } from '@/modules/projects';
import { axiosInstance } from '@/utils/axios';

import { mapGeneratedInvoicesFromResponse } from '../mappers';
import { GenerateInvoiceListItem, GetGeneratedInvoicesResponse } from '../types';

interface GetGenerateInvoiceDataParams {
  cursor?: number;
  filters?: {
    searchString?: string;
    startDate?: string;
    endDate?: string;
    clientIds?: number[];
    projectIds?: number[];
    billingModels?: ProjectBillingModel[];
  };
}

/**
 * Retrieves generated invoice data for projects within a date range.
 * Wraps GET /api/invoices/generated with server-side filtering.
 *
 * @param params - Cursor and filters (startDate, endDate, searchString, clientIds, projectIds)
 * @returns Paginated list of generate invoice items
 */
export const getGenerateInvoiceData = async ({
  filters = {},
}: GetGenerateInvoiceDataParams): Promise<PaginatedResponse<GenerateInvoiceListItem>> => {
  const { searchString, startDate, endDate, clientIds, projectIds, billingModels } = filters;

  if (!startDate || !endDate) {
    return {
      items: [],
      pagination: {
        total: 0,
        cursor: null,
      },
    };
  }

  const endpoint = '/api/invoices/generated';

  const params: Record<string, string> = {
    startDate,
    endDate,
  };

  if (searchString) {
    params.search = searchString;
  }

  if (clientIds?.length) {
    params.clientIds = clientIds.join(',');
  }

  if (projectIds?.length) {
    params.projectIds = projectIds.join(',');
  }

  if (billingModels?.length) {
    params.billingModels = billingModels.join(',');
  }

  const response = await withApiErrorHandler(
    endpoint,
    ApiErrorCode.INVOICES_GENERATED_LOAD_FAILED,
    () =>
      axiosInstance.get<{
        data: GetGeneratedInvoicesResponse;
        error?: { status: number; message: string; code: string };
      }>(endpoint, { params })
  );

  const items = mapGeneratedInvoicesFromResponse(response);

  return {
    items,
    pagination: {
      total: items.length,
      cursor: null,
    },
  };
};
